// Platform billing (admin-portal) — KaziPeople billing-service via the gateway.
import axios from 'axios';

const API_V1 = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1').replace(
  /\/api(\/v1)?\/?$/,
  '/api/v1',
);
const B = `${API_V1}/billing/admin`;

export interface BillingOverview {
  mrr: number;
  arr: number;
  totalOutstanding: number;
  pastDueAmount: number;
  paidThisMonth: number;
  failedPayments: number;
  customersPastDue: number;
  customersSuspended: number;
  accountsByStatus: Record<string, number>;
}
export interface BillingAccount {
  id: string;
  tenantId: string;
  tenantNameSnapshot?: string;
  tenantCountry?: string;
  billingEmail?: string;
  currency: string;
  accountStatus: string;
  currentBalance: string;
  creditBalance?: string;
  paymentTermsDays: number;
  gracePeriodDays: number;
  contractStartDate?: string;
  contractEndDate?: string;
  lastReinstatementFeeAmount?: string;
}
export interface Subscription {
  id: string;
  billingCycle: string;
  amount: string;
  currency: string;
  status: string;
  nextInvoiceDate?: string;
  defaultPaymentProvider: string;
  allowManualPayment: boolean;
  allowAzampay: boolean;
  allowStripe: boolean;
}
export interface Invoice {
  id: string;
  invoiceNumber: string;
  totalAmount: string;
  amountPaid: string;
  balanceDue: string;
  currency: string;
  status: string;
  dueDate?: string;
  issueDate?: string;
}

export const getOverview = () => axios.get<BillingOverview>(`${B}/overview`).then((r) => r.data);
export const getTenants = (status?: string) =>
  axios.get<BillingAccount[]>(`${B}/tenants`, { params: status ? { status } : {} }).then((r) => r.data);
export const getTenant = (tenantId: string) =>
  axios
    .get<{ account: BillingAccount; subscription: Subscription | null; invoices: Invoice[] }>(
      `${B}/tenants/${tenantId}`,
    )
    .then((r) => r.data);
export const upsertSubscription = (tenantId: string, body: Record<string, any>) =>
  axios.put(`${B}/tenants/${tenantId}/subscription`, body).then((r) => r.data);
// Called by the onboarding wizard after the tenant is created — race-safe (ensures
// the billing account exists) account + subscription provisioning.
export const onboardBilling = (
  tenantId: string,
  body: { amount: number; billingCycle?: 'monthly' | 'yearly'; currency?: string; billingEmail?: string; tenantName?: string; planTier?: string },
) => axios.post(`${B}/tenants/${tenantId}/onboard-billing`, body).then((r) => r.data);
// Edit the account-level config (currency, country, billing email, terms, grace).
export const updateAccount = (
  tenantId: string,
  body: {
    tenantCountry?: string;
    currency?: string;
    billingEmail?: string;
    paymentTermsDays?: number;
    gracePeriodDays?: number;
  },
) => axios.patch(`${B}/tenants/${tenantId}/account`, body).then((r) => r.data);
// Apply a credit (+) or debit (-) adjustment to the tenant's credit balance.
export const adjustCredit = (tenantId: string, body: { amount: number; reason: string }) =>
  axios.post(`${B}/tenants/${tenantId}/credit-adjustment`, body).then((r) => r.data);
export const generateInvoice = (tenantId: string) =>
  axios.post(`${B}/tenants/${tenantId}/invoices/generate`).then((r) => r.data);
export const sendInvoice = (invoiceId: string) =>
  axios.post(`${B}/invoices/${invoiceId}/send`).then((r) => r.data);
// Canonical PDF — short-lived signed download URL (stored in Tigris/S3).
export const getInvoicePdf = (invoiceId: string) =>
  axios.get<{ url?: string; filename?: string }>(`${B}/invoices/${invoiceId}/pdf`).then((r) => r.data);
export const voidInvoice = (invoiceId: string, reason?: string) =>
  axios.post(`${B}/invoices/${invoiceId}/void`, { reason }).then((r) => r.data);
export const recordManualPayment = (body: Record<string, any>) =>
  axios.post(`${B}/payments/manual`, body).then((r) => r.data);
export const suspendAccount = (tenantId: string, reason?: string) =>
  axios.post(`${B}/accounts/${tenantId}/suspend`, { reason }).then((r) => r.data);
export const reactivateAccount = (tenantId: string) =>
  axios.post(`${B}/accounts/${tenantId}/reactivate`).then((r) => r.data);
export const endContract = (tenantId: string, reason?: string) =>
  axios.post(`${B}/accounts/${tenantId}/end-contract`, { reason }).then((r) => r.data);
export const waiveReinstatementFee = (tenantId: string) =>
  axios.post(`${B}/accounts/${tenantId}/waive-reinstatement-fee`).then((r) => r.data);
export const getRevenue = () => axios.get(`${B}/reports/revenue`).then((r) => r.data);
export const getAging = () => axios.get(`${B}/reports/aging`).then((r) => r.data);
export const getPaymentsReport = () => axios.get(`${B}/reports/payments`).then((r) => r.data);
// --- Plan catalog ---
export interface Plan {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  defaultMonthlyAmount: string;
  defaultYearlyAmount: string;
  currency: string;
  isActive: boolean;
  features: string[];
}
export const getPlans = () => axios.get<Plan[]>(`${B}/plans`).then((r) => r.data);
export const createPlan = (body: Partial<Plan> & { code: string; name: string }) =>
  axios.post<Plan>(`${B}/plans`, body).then((r) => r.data);
export const updatePlan = (id: string, body: Partial<Plan>) =>
  axios.patch<Plan>(`${B}/plans/${id}`, body).then((r) => r.data);

// --- Renewals ---
export interface UpcomingInvoice {
  id: string;
  tenantId: string;
  tenantName?: string | null;
  amount: string;
  currency: string;
  billingCycle: string;
  nextInvoiceDate?: string;
  status: string;
  defaultPaymentProvider: string;
  planId?: string | null;
}
export interface ExpiringContract {
  tenantId: string;
  tenantNameSnapshot?: string | null;
  tenantCountry?: string | null;
  contractEndDate?: string;
  accountStatus: string;
}
export interface Renewals {
  withinDays: number;
  upcomingInvoices: UpcomingInvoice[];
  expiringContracts: ExpiringContract[];
}
export const getRenewals = (withinDays = 30) =>
  axios.get<Renewals>(`${B}/renewals`, { params: { withinDays } }).then((r) => r.data);

export const getSettings = () => axios.get(`${B}/settings`).then((r) => r.data);
export const updateSettings = (body: Record<string, any>) =>
  axios.put(`${B}/settings`, body).then((r) => r.data);

// --- Plan versions (commercial packaging Phase 1: immutable, relational contents) ---
export interface PlanVersionEntitlement {
  id?: string;
  productCode: string;
  limits?: Record<string, unknown>;
  configuration?: Record<string, unknown>;
}
export interface PlanPrice {
  id?: string;
  currency: string;
  billingPeriod: 'monthly' | 'yearly';
  amount: string | number;
}
export interface PlanVersion {
  id: string;
  planId: string;
  version: number;
  status: 'draft' | 'published' | 'retired';
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  changeReason?: string | null;
  entitlements: PlanVersionEntitlement[];
  prices: PlanPrice[];
}
export const listPlanVersions = (planId: string) =>
  axios.get<PlanVersion[]>(`${B}/plans/${planId}/versions`).then((r) => r.data);
export const createPlanVersion = (planId: string, body: { entitlements?: PlanVersionEntitlement[]; prices?: PlanPrice[]; changeReason?: string }) =>
  axios.post<PlanVersion>(`${B}/plans/${planId}/versions`, body).then((r) => r.data);
export const updatePlanVersion = (versionId: string, body: { entitlements?: PlanVersionEntitlement[]; prices?: PlanPrice[]; changeReason?: string }) =>
  axios.put<PlanVersion>(`${B}/plan-versions/${versionId}`, body).then((r) => r.data);
export const publishPlanVersion = (versionId: string) =>
  axios.post<PlanVersion>(`${B}/plan-versions/${versionId}/publish`).then((r) => r.data);

// --- Subscription add-on items (Phase 3) ---
export interface SubscriptionItem {
  id: string;
  subscriptionId: string;
  productCode: string;
  limits?: Record<string, unknown>;
  priceAmount: string;
  currency: string;
  note?: string | null;
}
export const listSubscriptionItems = (subscriptionId: string) =>
  axios.get<SubscriptionItem[]>(`${B}/subscriptions/${subscriptionId}/items`).then((r) => r.data);
export const addSubscriptionItem = (subscriptionId: string, body: { productCode: string; limits?: Record<string, unknown>; priceAmount?: number; note?: string }) =>
  axios.post<SubscriptionItem>(`${B}/subscriptions/${subscriptionId}/items`, body).then((r) => r.data);
export const removeSubscriptionItem = (itemId: string) =>
  axios.delete(`${B}/subscription-items/${itemId}`).then((r) => r.data);
export const pushTenantEntitlements = (tenantId: string) =>
  axios.post(`${B}/tenants/${tenantId}/entitlements/push`).then((r) => r.data);

// --- Service orders (Phase 4: one-time professional services) ---
export interface ServiceOrder {
  id: string;
  tenantId: string;
  type: string;
  description: string;
  amount: string;
  currency: string;
  status: 'draft' | 'approved' | 'delivered' | 'invoiced' | 'cancelled';
  linkedInvoiceId?: string | null;
  contractRef?: string | null;
  createdAt: string;
}
export const listServiceOrders = (tenantId?: string) =>
  axios.get<ServiceOrder[]>(tenantId ? `${B}/tenants/${tenantId}/service-orders` : `${B}/service-orders`).then((r) => r.data);
export const createServiceOrder = (body: { tenantId: string; type: string; description: string; amount: number; currency?: string; contractRef?: string }) =>
  axios.post<ServiceOrder>(`${B}/service-orders`, body).then((r) => r.data);
export const transitionServiceOrder = (orderId: string, action: 'approve' | 'deliver' | 'cancel') =>
  axios.post<ServiceOrder>(`${B}/service-orders/${orderId}/${action}`).then((r) => r.data);
export const invoiceServiceOrder = (orderId: string) =>
  axios.post<{ invoiceId?: string; invoiceNumber?: string; error?: string }>(`${B}/service-orders/${orderId}/invoice`).then((r) => r.data);
